import { getFunders, type Funder } from './funder';

export type ClientFundingSourceProgram =
  | Funder
  | 'STRC'
  | 'RESTORATIVE_CARE_PATHWAY'
  | 'END_OF_LIFE_PATHWAY'
  | 'UNKNOWN';

export const CLIENT_FUNDING_SOURCE_PROGRAMS: readonly ClientFundingSourceProgram[] = [
  'SUPPORT_AT_HOME',
  'HCP',
  'CHSP',
  'NATSIFACP',
  'DVA',
  'NDIS',
  'TAC',
  'PRIVATE',
  'AN_ACC',
  'STRC',
  'RESTORATIVE_CARE_PATHWAY',
  'END_OF_LIFE_PATHWAY',
  'UNKNOWN',
];

export function isClientFundingSourceProgram(value: unknown): value is ClientFundingSourceProgram {
  return (CLIENT_FUNDING_SOURCE_PROGRAMS as readonly string[]).includes(String(value ?? '').trim());
}

export type ClientFundingCohort =
  | 'SAH_STANDARD'
  | 'SAH_INTERIM_MSO'
  | 'HCP_TRANSITION_NO_WORSE_OFF'
  | 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS'
  | 'CHSP_PRE_2027'
  | 'STRC_PRE_2025'
  | 'SAH_RESTORATIVE_PATHWAY'
  | 'SAH_END_OF_LIFE_PATHWAY'
  | 'RESIDENTIAL_AN_ACC'
  | 'NON_SAH_FUNDED'
  | 'UNKNOWN';

export const CLIENT_FUNDING_COHORTS: readonly ClientFundingCohort[] = [
  'SAH_STANDARD',
  'SAH_INTERIM_MSO',
  'HCP_TRANSITION_NO_WORSE_OFF',
  'HCP_TRANSITION_STANDARD_CONTRIBUTIONS',
  'CHSP_PRE_2027',
  'STRC_PRE_2025',
  'SAH_RESTORATIVE_PATHWAY',
  'SAH_END_OF_LIFE_PATHWAY',
  'RESIDENTIAL_AN_ACC',
  'NON_SAH_FUNDED',
  'UNKNOWN',
];

export const CLIENT_FUNDING_COHORT_LABELS: Record<ClientFundingCohort, string> = {
  SAH_STANDARD: 'Standard SAH classification',
  SAH_INTERIM_MSO: 'Minimum Service Offer / interim funding',
  HCP_TRANSITION_NO_WORSE_OFF: 'HCP transition - no worse off',
  HCP_TRANSITION_STANDARD_CONTRIBUTIONS: 'HCP transition - standard contributions',
  CHSP_PRE_2027: 'CHSP continuing - transition no earlier than 1 July 2027',
  STRC_PRE_2025: 'STRC commenced before 1 Nov 2025',
  SAH_RESTORATIVE_PATHWAY: 'SAH restorative pathway',
  SAH_END_OF_LIFE_PATHWAY: 'SAH end-of-life pathway',
  RESIDENTIAL_AN_ACC: 'Residential AN-ACC',
  NON_SAH_FUNDED: 'Non-SAH funded',
  UNKNOWN: 'Unknown funding cohort',
};

export type ClientFundingTransitionCutoffBasis =
  | 'HCP_APPROVED_ON_OR_BEFORE_2024_09_12'
  | 'HCP_APPROVED_AFTER_2024_09_12_BEFORE_2025_11_01'
  | 'HCP_OR_NPS_ON_2025_10_31'
  | 'CHSP_TRANSITION_NO_EARLIER_THAN_2027_07_01'
  | 'STRC_COMMENCED_BEFORE_2025_11_01'
  | 'NOT_APPLICABLE'
  | 'UNKNOWN';

export const CLIENT_FUNDING_TRANSITION_CUTOFF_BASES: readonly ClientFundingTransitionCutoffBasis[] =
  [
    'HCP_APPROVED_ON_OR_BEFORE_2024_09_12',
    'HCP_APPROVED_AFTER_2024_09_12_BEFORE_2025_11_01',
    'HCP_OR_NPS_ON_2025_10_31',
    'CHSP_TRANSITION_NO_EARLIER_THAN_2027_07_01',
    'STRC_COMMENCED_BEFORE_2025_11_01',
    'NOT_APPLICABLE',
    'UNKNOWN',
  ];

export const HCP_NO_WORSE_OFF_APPROVAL_CUTOFF = '2024-09-12';

export function isHcpNoWorseOffApprovalDate(value: string | null | undefined): boolean {
  const date = typeof value === 'string' ? value.trim().slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= HCP_NO_WORSE_OFF_APPROVAL_CUTOFF;
}

export type ClientFundingOfferStatus =
  | 'NOT_APPLICABLE'
  | 'PRE_FUNDING'
  | 'MINIMUM_SERVICE_OFFER'
  | 'FULL_SERVICE_OFFER'
  | 'DECLINED'
  | 'UNKNOWN';

export const CLIENT_FUNDING_OFFER_STATUSES: readonly ClientFundingOfferStatus[] = [
  'NOT_APPLICABLE',
  'PRE_FUNDING',
  'MINIMUM_SERVICE_OFFER',
  'FULL_SERVICE_OFFER',
  'DECLINED',
  'UNKNOWN',
];

export const CLIENT_FUNDING_OFFER_STATUS_LABELS: Record<ClientFundingOfferStatus, string> = {
  NOT_APPLICABLE: 'Not applicable',
  PRE_FUNDING: 'Pre-funding / awaiting assignment',
  MINIMUM_SERVICE_OFFER: 'Minimum Service Offer',
  FULL_SERVICE_OFFER: 'Full Service Offer',
  DECLINED: 'Declined',
  UNKNOWN: 'Unknown',
};

export function isClientFundingOfferStatus(value: unknown): value is ClientFundingOfferStatus {
  return (CLIENT_FUNDING_OFFER_STATUSES as readonly string[]).includes(String(value ?? '').trim());
}

export type ClientMsoFsoStatus =
  | 'NOT_APPLICABLE'
  | 'MSO_ACTIVE'
  | 'FSO_PENDING'
  | 'FSO_ACCEPTED'
  | 'FSO_DECLINED'
  | 'UNKNOWN';

export const CLIENT_MSO_FSO_STATUSES: readonly ClientMsoFsoStatus[] = [
  'NOT_APPLICABLE',
  'MSO_ACTIVE',
  'FSO_PENDING',
  'FSO_ACCEPTED',
  'FSO_DECLINED',
  'UNKNOWN',
];

export const CLIENT_MSO_FSO_STATUS_LABELS: Record<ClientMsoFsoStatus, string> = {
  NOT_APPLICABLE: 'Not applicable',
  MSO_ACTIVE: 'MSO active',
  FSO_PENDING: 'FSO pending',
  FSO_ACCEPTED: 'FSO accepted',
  FSO_DECLINED: 'FSO declined',
  UNKNOWN: 'Unknown',
};

export function isClientMsoFsoStatus(value: unknown): value is ClientMsoFsoStatus {
  return (CLIENT_MSO_FSO_STATUSES as readonly string[]).includes(String(value ?? '').trim());
}

export function isClientFundingCohort(value: unknown): value is ClientFundingCohort {
  return (CLIENT_FUNDING_COHORTS as readonly string[]).includes(String(value ?? '').trim());
}

export function isHcpTransitionFundingCohort(
  cohort: ClientFundingCohort | null | undefined,
): boolean {
  return (
    cohort === 'HCP_TRANSITION_NO_WORSE_OFF' || cohort === 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS'
  );
}

export function isNoWorseOffFundingCohort(cohort: ClientFundingCohort | null | undefined): boolean {
  return cohort === 'HCP_TRANSITION_NO_WORSE_OFF';
}

/**
 * Funding cohorts that operate under the Support at Home money machinery
 * (classification, quarter budget, contribution, statements, claims) — including
 * the HCP→SAH transition population, who are genuinely becoming SAH clients.
 */
export const SAH_MACHINERY_COHORTS: ReadonlySet<ClientFundingCohort> = new Set([
  'SAH_STANDARD',
  'SAH_INTERIM_MSO',
  'HCP_TRANSITION_NO_WORSE_OFF',
  'HCP_TRANSITION_STANDARD_CONTRIBUTIONS',
  'SAH_RESTORATIVE_PATHWAY',
  'SAH_END_OF_LIFE_PATHWAY',
]);

/**
 * Whether a client should be run through the Support at Home money machinery.
 *
 * This is the surgical replacement for `isSahFundedClient` at the de-block
 * decision points: it is identical to `isSahFundedClient` EXCEPT a genuine
 * ongoing Home Care Package client — an `HCP` funder token with NO SAH /
 * HCP-transition cohort — returns `false` (so they get the HCP budget/agreement
 * path, not SAH). A true SAH client, an HCP client mid-transition (any
 * `*_TRANSITION_*` / SAH cohort), and a legacy un-backfilled client all return
 * `true` exactly as before — so the change is non-regressive for SAH.
 *
 * Distinguishing signal, in order:
 *   1. SUPPORT_AT_HOME present              → true (true SAH)
 *   2. an explicit SAH / transition cohort  → true (transitioning population)
 *   3. HCP token (no SAH cohort)            → false (genuine ongoing HCP)
 *   4. any other recognised non-SAH funder  → false (CHSP/NDIS/DVA/PRIVATE/…)
 *   5. no recognised funder, no cohort      → true (fail OPEN; mirrors
 *      isSahFundedClient's legacy behaviour — never strand an un-backfilled SAH row)
 */
export function clientRequiresSahMachinery(input: {
  fundingPrograms: readonly string[] | null | undefined;
  fundingCohort?: ClientFundingCohort | null;
}): boolean {
  const funders = getFunders(input.fundingPrograms);
  if (funders.includes('SUPPORT_AT_HOME')) return true;
  if (input.fundingCohort && SAH_MACHINERY_COHORTS.has(input.fundingCohort)) return true;
  if (funders.includes('HCP')) return false;
  if (funders.length > 0) return false;
  return true;
}

/**
 * Positive SAH-machinery signal — true ONLY when there is an explicit SAH basis:
 * a SUPPORT_AT_HOME funder, or a SAH/HCP-transition machinery cohort. Unlike
 * clientRequiresSahMachinery (which fails OPEN to true for empty funders), this
 * NEVER grants SAH machinery for NON_SAH_FUNDED / UNKNOWN / empty inputs. Use
 * this where fail-open is unsafe (e.g. the care-plan composer funding gating).
 */
export function hasPositiveSahMachinerySignal(input: {
  fundingPrograms: readonly string[] | null | undefined;
  fundingCohort?: ClientFundingCohort | null;
}): boolean {
  const funders = getFunders(input.fundingPrograms);
  if (funders.includes('SUPPORT_AT_HOME')) return true;
  if (input.fundingCohort && SAH_MACHINERY_COHORTS.has(input.fundingCohort)) return true;
  return false;
}

export function cohortRequiresSahClassification(
  cohort: ClientFundingCohort | null | undefined,
): boolean {
  return cohort === 'SAH_INTERIM_MSO';
}

export function cohortCanRegisterPendingSahClassification(
  cohort: ClientFundingCohort | null | undefined,
): boolean {
  return cohort === 'SAH_STANDARD';
}

export function defaultTransitionCutoffBasisForCohort(
  cohort: ClientFundingCohort | null | undefined,
): ClientFundingTransitionCutoffBasis {
  switch (cohort) {
    case 'HCP_TRANSITION_NO_WORSE_OFF':
      return 'HCP_APPROVED_ON_OR_BEFORE_2024_09_12';
    case 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS':
      return 'HCP_APPROVED_AFTER_2024_09_12_BEFORE_2025_11_01';
    case 'CHSP_PRE_2027':
      return 'CHSP_TRANSITION_NO_EARLIER_THAN_2027_07_01';
    case 'STRC_PRE_2025':
      return 'STRC_COMMENCED_BEFORE_2025_11_01';
    default:
      return 'NOT_APPLICABLE';
  }
}

export function defaultFundingOfferStatusForCohort(
  cohort: ClientFundingCohort | null | undefined,
  hasClassification = false,
): ClientFundingOfferStatus {
  if (cohort === 'SAH_INTERIM_MSO') return 'MINIMUM_SERVICE_OFFER';
  if (cohort === 'SAH_STANDARD') {
    return hasClassification ? 'FULL_SERVICE_OFFER' : 'PRE_FUNDING';
  }
  if (cohort === 'SAH_RESTORATIVE_PATHWAY' || cohort === 'SAH_END_OF_LIFE_PATHWAY') {
    return 'PRE_FUNDING';
  }
  return 'NOT_APPLICABLE';
}

export function defaultMsoFsoStatusForCohort(
  cohort: ClientFundingCohort | null | undefined,
): ClientMsoFsoStatus {
  return cohort === 'SAH_INTERIM_MSO' ? 'MSO_ACTIVE' : 'NOT_APPLICABLE';
}

export function defaultCohortForFundingPrograms(
  fundingPrograms: readonly string[] | null | undefined,
): ClientFundingCohort {
  const funders = getFunders(fundingPrograms);
  if (funders.includes('SUPPORT_AT_HOME')) return 'SAH_STANDARD';
  if (funders.includes('HCP')) return 'NON_SAH_FUNDED'; // bare HCP not transition (D13)
  if (funders.includes('CHSP')) return 'CHSP_PRE_2027';
  if (funders.includes('AN_ACC') || funders.includes('RAC_RESPITE')) return 'RESIDENTIAL_AN_ACC';
  if (funders.length > 0) return 'NON_SAH_FUNDED';
  return 'UNKNOWN';
}

export function sourceProgramForFundingContext(input: {
  cohort: ClientFundingCohort;
  fundingPrograms?: readonly string[] | null;
}): ClientFundingSourceProgram {
  switch (input.cohort) {
    case 'HCP_TRANSITION_NO_WORSE_OFF':
    case 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS':
      return 'HCP';
    case 'CHSP_PRE_2027':
      return 'CHSP';
    case 'STRC_PRE_2025':
      return 'STRC';
    case 'SAH_RESTORATIVE_PATHWAY':
      return 'RESTORATIVE_CARE_PATHWAY';
    case 'SAH_END_OF_LIFE_PATHWAY':
      return 'END_OF_LIFE_PATHWAY';
    case 'RESIDENTIAL_AN_ACC':
      return 'AN_ACC';
    case 'NON_SAH_FUNDED':
      return getFunders(input.fundingPrograms)[0] ?? 'UNKNOWN';
    default:
      return 'SUPPORT_AT_HOME';
  }
}

export type ClientFundingContextProjection = {
  cohort: ClientFundingCohort | null;
  sourceProgram: ClientFundingSourceProgram | null;
  fundingOfferStatus: ClientFundingOfferStatus | null;
  msoFsoStatus: ClientMsoFsoStatus | null;
  approvalDate?: string | null;
  firstHcpApprovalDate?: string | null;
  transitionCutoffBasis?: ClientFundingTransitionCutoffBasis | null;
};

export type ResolvedClientFundingContext = {
  cohort: ClientFundingCohort;
  cohortLabel: string;
  sourceProgram: ClientFundingSourceProgram;
  fundingOfferStatus: ClientFundingOfferStatus;
  fundingOfferStatusLabel: string;
  msoFsoStatus: ClientMsoFsoStatus;
  msoFsoStatusLabel: string;
  isTransitioner: boolean;
  isNoWorseOff: boolean;
  hasSahClassificationEvidence: boolean;
  approvalDate: string | null;
  firstHcpApprovalDate: string | null;
  transitionCutoffBasis: ClientFundingTransitionCutoffBasis;
};

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function isClientFundingTransitionCutoffBasis(
  value: unknown,
): value is ClientFundingTransitionCutoffBasis {
  return (CLIENT_FUNDING_TRANSITION_CUTOFF_BASES as readonly string[]).includes(
    String(value ?? '').trim(),
  );
}

export function clientFundingContextProjectionFromRecord(
  row: Record<string, unknown> | null | undefined,
): ClientFundingContextProjection | null {
  if (!row) return null;
  const cohort = isClientFundingCohort(row.cohort) ? row.cohort : null;
  const sourceProgramRaw = row.source_program ?? row.sourceProgram;
  const fundingOfferStatusRaw = row.funding_offer_status ?? row.fundingOfferStatus;
  const msoFsoStatusRaw = row.mso_fso_status ?? row.msoFsoStatus;
  const transitionCutoffRaw = row.transition_cutoff_basis ?? row.transitionCutoffBasis;
  return {
    cohort,
    sourceProgram: isClientFundingSourceProgram(sourceProgramRaw) ? sourceProgramRaw : null,
    fundingOfferStatus: isClientFundingOfferStatus(fundingOfferStatusRaw)
      ? fundingOfferStatusRaw
      : null,
    msoFsoStatus: isClientMsoFsoStatus(msoFsoStatusRaw) ? msoFsoStatusRaw : null,
    approvalDate: stringOrNull(row.approval_date ?? row.approvalDate),
    firstHcpApprovalDate: stringOrNull(row.first_hcp_approval_date ?? row.firstHcpApprovalDate),
    transitionCutoffBasis: isClientFundingTransitionCutoffBasis(transitionCutoffRaw)
      ? transitionCutoffRaw
      : null,
  };
}

function fallbackCohortForClient(input: {
  fundingPrograms?: readonly string[] | null;
  transitionedHcpLevel?: number | null;
  isGrandfatheredHcp?: boolean | null;
  sahIsGrandfathered?: boolean | null;
}): ClientFundingCohort {
  if (input.transitionedHcpLevel != null) {
    return input.sahIsGrandfathered === true || input.isGrandfatheredHcp === true
      ? 'HCP_TRANSITION_NO_WORSE_OFF'
      : 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS';
  }
  return defaultCohortForFundingPrograms(input.fundingPrograms);
}

export function resolveClientFundingContext(input: {
  fundingPrograms?: readonly string[] | null;
  fundingContext?: ClientFundingContextProjection | null;
  hasSahClassificationRecord?: boolean;
  classificationLevel?: number | null;
  transitionedHcpLevel?: number | null;
  isGrandfatheredHcp?: boolean | null;
  sahIsGrandfathered?: boolean | null;
}): ResolvedClientFundingContext {
  const cohort =
    input.fundingContext?.cohort ??
    fallbackCohortForClient({
      fundingPrograms: input.fundingPrograms,
      transitionedHcpLevel: input.transitionedHcpLevel,
      isGrandfatheredHcp: input.isGrandfatheredHcp,
      sahIsGrandfathered: input.sahIsGrandfathered,
    });
  const hasSahClassificationEvidence =
    Boolean(input.hasSahClassificationRecord) ||
    input.classificationLevel != null ||
    input.transitionedHcpLevel != null;
  const sourceProgram =
    input.fundingContext?.sourceProgram ??
    sourceProgramForFundingContext({ cohort, fundingPrograms: input.fundingPrograms });
  const fundingOfferStatus =
    input.fundingContext?.fundingOfferStatus ??
    defaultFundingOfferStatusForCohort(cohort, hasSahClassificationEvidence);
  const msoFsoStatus = input.fundingContext?.msoFsoStatus ?? defaultMsoFsoStatusForCohort(cohort);
  const isTransitioner = isHcpTransitionFundingCohort(cohort) || input.transitionedHcpLevel != null;
  const hasNoWorseOffTransitionEvidence =
    input.sahIsGrandfathered === true ||
    input.isGrandfatheredHcp === true ||
    isHcpNoWorseOffApprovalDate(input.fundingContext?.firstHcpApprovalDate);
  const hasStandardTransitionEvidence =
    input.fundingContext?.transitionCutoffBasis ===
    'HCP_APPROVED_AFTER_2024_09_12_BEFORE_2025_11_01';
  const isNoWorseOff =
    cohort === 'HCP_TRANSITION_NO_WORSE_OFF'
      ? hasNoWorseOffTransitionEvidence
      : cohort === 'HCP_TRANSITION_STANDARD_CONTRIBUTIONS'
        ? false
        : hasNoWorseOffTransitionEvidence
          ? true
          : hasStandardTransitionEvidence
            ? false
            : input.sahIsGrandfathered === true || input.isGrandfatheredHcp === true;

  return {
    cohort,
    cohortLabel: CLIENT_FUNDING_COHORT_LABELS[cohort],
    sourceProgram,
    fundingOfferStatus,
    fundingOfferStatusLabel: CLIENT_FUNDING_OFFER_STATUS_LABELS[fundingOfferStatus],
    msoFsoStatus,
    msoFsoStatusLabel: CLIENT_MSO_FSO_STATUS_LABELS[msoFsoStatus],
    isTransitioner,
    isNoWorseOff,
    hasSahClassificationEvidence,
    approvalDate: input.fundingContext?.approvalDate ?? null,
    firstHcpApprovalDate: input.fundingContext?.firstHcpApprovalDate ?? null,
    transitionCutoffBasis:
      input.fundingContext?.transitionCutoffBasis ?? defaultTransitionCutoffBasisForCohort(cohort),
  };
}
